import SoftphoneDriver, { requestMicrophone } from "./SoftphoneDriver";

/**
 * StubDriver — a softphone that behaves like a real one but places no call.
 *
 * **Not a throwaway.** It exists for three jobs that outlive the Exotel
 * integration:
 *
 *  1. The entire softphone — state machine, widget, timer, mute/hold, DTMF,
 *     fallback-to-phone path — was built and exercised against this before any
 *     vendor SDK existed, which is why none of that work was blocked on
 *     credentials.
 *  2. It stays as the test double. A driver-shaped fake is the only way to
 *     assert "registration dropped ⇒ the next call falls back to the phone"
 *     without a billable call.
 *  3. It is what a demo runs on.
 *
 * It deliberately takes the microphone for real. Permission is the single most
 * common reason browser calling fails on a counsellor's machine, and a stub that
 * skipped it would make the flow look fine right up until it wasn't.
 */

/** Enough delay to see a state, short enough not to be tedious. */
const RING_MS = 1200;
const CONNECT_MS = 1500;

export default class StubDriver extends SoftphoneDriver {
  constructor({ autoAnswer = false } = {}) {
    super();
    this._registered = false;
    this._call = null;
    this._timers = [];
    this._stream = null;
    this._autoAnswer = autoAnswer;
  }

  get name() { return "stub"; }

  async isAvailable() {
    return { ok: true };
  }

  async register({ sipUri, sipIdentity, displayName, onEvent } = {}) {
    this._onEvent = onEvent || (() => {});
    this._emit({ type: "registering" });

    const mic = await requestMicrophone();
    if (!mic.ok) {
      this._emit({ type: "registration_failed", message: mic.reason });
      return { ok: false, reason: mic.reason };
    }
    this._stream = mic.stream;

    this._registered = true;
    this._identity = sipIdentity || sipUri || displayName || "stub-agent";
    this._emit({ type: "registered" });
    return { ok: true };
  }

  async unregister() {
    this._clearTimers();
    // Release the mic explicitly. Leaving the track live keeps the browser's
    // recording indicator on after the counsellor switched back to phone mode,
    // which looks exactly like being silently listened to.
    this._stream?.getTracks?.().forEach((t) => t.stop());
    this._stream = null;
    this._registered = false;
    this._call = null;
    this._emit({ type: "unregistered" });
  }

  /**
   * Simulate the leg the provider would ring after `initiateCall` is accepted.
   * The real driver receives this from the network; here the caller triggers it.
   */
  simulateIncoming({ id = `stub-${Date.now()}`, from = "+919876543210", direction = "outbound" } = {}) {
    if (!this._registered) return;
    this._call = { id, from, to: from, direction, muted: false, held: false };
    this._emit({ type: direction === "inbound" ? "incoming" : "ringing", call: this._call });

    if (this._autoAnswer) {
      this._timers.push(setTimeout(() => this.answer(), RING_MS));
    }
  }

  async answer() {
    if (!this._call) return;
    this._call = { ...this._call, answeredAt: Date.now() };
    this._emit({ type: "answered", call: this._call });

    // A stub call that never ends would leave a timer running all afternoon.
    this._timers.push(setTimeout(() => this.hangup("remote"), CONNECT_MS * 20));
  }

  async hangup(reason = "local") {
    if (!this._call) return;
    const call = this._call;
    this._call = null;
    this._clearTimers();
    this._emit({ type: "ended", call, reason });
  }

  async mute(on) {
    if (!this._call) return;
    this._call = { ...this._call, muted: !!on };
    // Mute the real track: a mute button that lies is worse than no mute button.
    this._stream?.getAudioTracks?.().forEach((t) => { t.enabled = !on; });
  }

  async hold(on) {
    if (!this._call) return;
    this._call = { ...this._call, held: !!on };
  }

  async sendDtmf(digit) {
    if (!this._call) return;
    console.info(`[Softphone stub] DTMF ${digit}`);
  }

  _clearTimers() {
    this._timers.forEach(clearTimeout);
    this._timers = [];
  }
}
